import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FlaskConical, Search, List, FileText, CheckCircle, Clock } from 'lucide-react';

const operations = [
  {
    id: 'research_topic',
    name: 'Research Topic',
    description: 'Gather information and insights on a specific subject',
    icon: Search,
    color: 'purple',
    input: 'Topic keywords, research scope',
    output: 'Research findings and insights',
    example: 'AI trends → Comprehensive research report'
  },
  {
    id: 'outline_content',
    name: 'Create Outline',
    description: 'Structure content framework and organization',
    icon: List,
    color: 'blue',
    input: 'Content topic, target audience',
    output: 'Structured outline',
    example: 'Blog post idea → Detailed outline'
  },
  {
    id: 'content_planning',
    name: 'Content Planning',
    description: 'Plan content strategy and execution approach',
    icon: FileText,
    color: 'green',
    input: 'Content goals, timeline',
    output: 'Content plan and strategy',
    example: 'Marketing goals → Content calendar'
  }
];

const getColorClasses = (color: string) => {
  const colors = {
    blue: { bg: 'bg-[var(--slate-dim)]', border: 'border-[var(--slate)]', text: 'text-[var(--text-sec)]', badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)]', hover: 'hover:bg-[var(--slate-dim)]' },
    green: { bg: 'bg-[var(--green-dim)]', border: 'border-[var(--green)]', text: 'text-[var(--green)] dark:text-[var(--text-ter)]', badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]', hover: 'hover:bg-[var(--green-dim)]' },
    purple: { bg: 'bg-[var(--slate-dim)]', border: 'border-[var(--slate)]', text: 'text-[var(--text-sec)]', badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)]', hover: 'hover:bg-[var(--slate-dim)]' }
  };
  return colors[color as keyof typeof colors] || colors.blue;
};

export default function Formulating() {
  const [selectedOperation, setSelectedOperation] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<any[]>([]);

  const handleOperation = async (operationId: string) => {
    setSelectedOperation(operationId);
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setResults(prev => [...prev, { id: Date.now(), operation: operationId, status: 'completed', timestamp: new Date().toISOString() }]);
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-[var(--surface)]">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <Link to="/elevation/workflow" className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to Workflow
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-[var(--slate-dim)] flex items-center justify-center">
              <FlaskConical className="h-6 w-6 text-[var(--text-ter)]" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-[var(--text-pri)]">Formulating Ideas</h1>
              <p className="text-[var(--text-sec)]">Research and develop concepts into structured content</p>
            </div>
          </div>
        </div>

        <div className="bg-[var(--bg-raised)] shadow-sm border p-6 mb-8">
          <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">Authoring Engine Status</h2>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-[var(--green)] rounded-full"></div>
              <span className="text-sm text-[var(--text-sec)]">Online</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-[var(--text-ter)]" />
              <span className="text-sm text-[var(--text-sec)]">Last activity: 3 minutes ago</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-[var(--green)]" />
              <span className="text-sm text-[var(--text-sec)]">Ready for operations</span>
            </div>
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-6">Available Operations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {operations.map((operation) => {
              const Icon = operation.icon;
              const colors = getColorClasses(operation.color);
              
              return (
                <div key={operation.id} className={`${colors.bg} ${colors.border} border-2  p-6 ${colors.hover} transition-all duration-300`}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-12 h-12 ${colors.badge}  flex items-center justify-center`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold ${colors.text}`}>{operation.name}</h3>
                      <p className={`text-sm ${colors.text}`}>{operation.description}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3 mb-4">
                    <div>
                      <span className="text-xs font-medium text-[var(--text-ter)]">INPUT:</span>
                      <p className="text-sm text-[var(--text-sec)]">{operation.input}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-[var(--text-ter)]">OUTPUT:</span>
                      <p className="text-sm text-[var(--text-sec)]">{operation.output}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-[var(--text-ter)]">EXAMPLE:</span>
                      <p className="text-sm text-[var(--text-sec)]">{operation.example}</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleOperation(operation.id)}
                    disabled={isProcessing}
                    className={`w-full py-2 px-4  font-medium transition-colors ${
                      isProcessing ? 'bg-[var(--rule-bright)] text-[var(--text-ter)] cursor-not-allowed' : `${colors.badge} ${colors.text} hover:opacity-80`
                    }`}
                  >
                    {isProcessing && selectedOperation === operation.id ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                        Processing...
                      </div>
                    ) : (
                      'Execute Operation'
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {results.length > 0 && (
          <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
            <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">Processing Results</h2>
            <div className="space-y-3">
              {results.map((result) => (
                <div key={result.id} className="flex items-center gap-3 p-3 bg-[var(--surface)]">
                  <CheckCircle className="h-5 w-5 text-[var(--green)]" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[var(--text-pri)]">
                      {operations.find(op => op.id === result.operation)?.name} completed
                    </p>
                    <p className="text-xs text-[var(--text-ter)]">{new Date(result.timestamp).toLocaleString()}</p>
                  </div>
                  <span className="text-xs bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] px-2 py-1 rounded">{result.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Next Steps</h3>
          <p className="text-[var(--text-sec)] mb-4">Once ideas are formulated, move to the creation phase to author and design content.</p>
          <Link to="/elevation/workflow/creating" className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--mustard)] text-white hover:bg-[var(--mustard)] transition-colors">
            Go to Creating Phase
            <ArrowLeft className="h-4 w-4 rotate-180" />
          </Link>
        </div>
      </div>
    </div>
  );
}
